import React, { useEffect, useState } from 'react';
import { employeeApi } from '@/lib/api';
import {
  applyBasicIncrement,
  buildIncrementSalaryRequest,
  salaryAfterBasicChange,
  summarizeSalaryStructure,
} from '@/lib/payrollCalc';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const COMPARISON_ROWS = [
  { key: 'basic', label: 'Basic' },
  { key: 'hra', label: 'House Rent Allowance', editable: true },
  { key: 'specialAllowance', label: 'Special Allowance', editable: true },
  { key: 'gross', label: 'Gross Salary (A)', bold: true },
  { key: 'conveyance', label: 'Conveyance', editable: true },
  { key: 'totalReimb', label: 'Total Reimbursements (B)', bold: true },
  { key: 'epf', label: 'Employee PF', editable: true },
  { key: 'esic', label: 'ESIC', editable: true },
  { key: 'tds', label: 'TDS', editable: true },
  { key: 'totalDed', label: 'Total Deductions (C)', bold: true },
  { key: 'inHand', label: 'In Hand Salary', tone: 'inhand' },
  { key: 'bonus', label: 'Bonus', editable: true },
  { key: 'employerPF', label: 'Employer PF', editable: true },
  { key: 'employerESIC', label: 'Employer ESIC', editable: true },
  { key: 'telephoneExpense', label: 'Telephone Expense', editable: true },
  { key: 'mediclaim', label: 'Mediclaim', editable: true },
  { key: 'gratuity', label: 'Gratuity', editable: true },
  { key: 'employerTotal', label: 'Employer Contributions (E)', bold: true },
  { key: 'ctc', label: 'CTC', tone: 'ctc' },
];

const fmt = (value) => Number(value || 0).toLocaleString('en-IN');

const emptyDraft = () => ({
  mode: 'percentage',
  value: '',
  effectiveMonth: String(new Date().getMonth() + 1),
  effectiveYear: String(new Date().getFullYear()),
  reason: '',
});

const validateDraft = (draft, basic) => {
  const value = Number(draft?.value);
  const mode = draft?.mode === 'amount' ? 'amount' : 'percentage';
  if (draft?.value === '' || draft?.value == null || !Number.isFinite(value) || value <= 0) {
    return mode === 'amount'
      ? 'Enter an increment amount greater than 0'
      : 'Enter an increment percentage greater than 0';
  }
  if (mode === 'percentage' && value > 1000) return 'Percentage cannot exceed 1000';
  const month = Number(draft?.effectiveMonth);
  if (!Number.isInteger(month) || month < 1 || month > 12) return 'Select an effective month';
  const year = Number(draft?.effectiveYear);
  if (!Number.isInteger(year) || year < 2000 || year > 2100) return 'Enter an effective year between 2000 and 2100';
  const nextBasic = applyBasicIncrement(basic, mode, value);
  if (nextBasic == null) return 'This increment does not increase basic salary. Enter a larger value.';
  return null;
};

const MoneyCell = ({ monthly, tone }) => {
  const toneClass = tone === 'inhand' ? 'text-[#10b981]' : tone === 'ctc' ? 'text-[#0047AB]' : '';
  return (
    <div className={toneClass}>
      <div className="font-mono text-xs sm:text-sm whitespace-nowrap">&#8377;{fmt(monthly)}</div>
      <div className="font-mono text-[10px] text-muted-foreground whitespace-nowrap">&#8377;{fmt(Number(monthly || 0) * 12)}/yr</div>
    </div>
  );
};

export function SalaryIncrementDialog({
  open,
  onOpenChange,
  employeeId,
  salary,
  wageCaps,
  onApplied,
}) {
  const [step, setStep] = useState('form');
  const [draft, setDraft] = useState(emptyDraft);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const currentBasic = Math.round(Number(salary?.basic) || 0);

  useEffect(() => {
    if (!open) return;
    setStep('form');
    setDraft(emptyDraft());
    setPreview(null);
    setSaving(false);
  }, [open]);

  const handleOpenChange = (next) => {
    if (saving) return;
    onOpenChange?.(next);
  };

  const handlePreview = (event) => {
    event?.preventDefault?.();
    const error = validateDraft(draft, currentBasic);
    if (error) {
      toast.error(error);
      return;
    }
    const mode = draft.mode === 'amount' ? 'amount' : 'percentage';
    const nextBasic = applyBasicIncrement(currentBasic, mode, draft.value);
    const current = summarizeSalaryStructure(salary);
    const next = salaryAfterBasicChange(salary, nextBasic, wageCaps);
    const request = buildIncrementSalaryRequest(next, draft);
    setPreview({ current, next, request, mode, value: Number(draft.value) });
    setStep('compare');
  };

  const handleConfirm = async () => {
    if (!employeeId || !preview?.request) return;
    setSaving(true);
    try {
      const result = await employeeApi.incrementSalary(employeeId, preview.request);
      const returnedSalary = result?.salary && typeof result.salary === 'object' ? result.salary : null;
      onApplied?.({
        ...(returnedSalary || {}),
        ...preview.request.salary,
      });
      toast.success('Salary increment saved');
      setSaving(false);
      onOpenChange?.(false);
    } catch (err) {
      toast.error(err?.message || 'Failed to save salary increment');
      setSaving(false);
    }
  };

  const handleRevisedChange = (key, raw) => {
    const parsed = Math.max(0, Math.round(Number(raw) || 0));
    setPreview((prev) => {
      if (!prev?.next) return prev;
      const next = summarizeSalaryStructure({ ...prev.next, [key]: parsed });
      return {
        ...prev,
        next,
        request: buildIncrementSalaryRequest(next, {
          effectiveMonth: prev.request?.effectiveMonth,
          effectiveYear: prev.request?.effectiveYear,
          reason: prev.request?.reason,
        }),
      };
    });
  };

  const monthLabel = MONTHS[(Number(preview?.request?.effectiveMonth) || 1) - 1] || '';
  const addition = (preview?.next?.basic || 0) - (preview?.current?.basic || 0);
  const reasonText = String(preview?.request?.reason ?? '').trim();

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="w-[calc(100%-1.5rem)] max-w-lg max-h-[90vh] overflow-y-auto sm:max-w-3xl"
        data-testid="increment-salary-dialog"
      >
        <DialogHeader className="pr-8">
          <DialogTitle className="font-[Manrope]">
            {step === 'compare' ? 'Review salary increment' : 'Increment salary'}
          </DialogTitle>
          <DialogDescription>
            {step === 'compare'
              ? 'Edit any revised amount except basic. Totals update from those amounts.'
              : 'Add a percentage or a fixed amount to basic salary. House rent, PF, ESIC, bonus, and gratuity update from the new basic.'}
          </DialogDescription>
        </DialogHeader>

        {step === 'form' ? (
          <form onSubmit={handlePreview} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="increment-value" className="text-xs uppercase tracking-widest font-medium text-muted-foreground">
                Increment
              </Label>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="flex shrink-0 gap-2" role="group" aria-label="Increment type">
                  <Button
                    type="button"
                    variant={draft.mode === 'percentage' ? 'default' : 'outline'}
                    aria-pressed={draft.mode === 'percentage'}
                    className={`px-4 ${draft.mode === 'percentage' ? 'bg-[#0047AB] hover:bg-[#003d96] text-white' : ''}`}
                    onClick={() => setDraft((prev) => ({ ...prev, mode: 'percentage' }))}
                    data-testid="increment-mode-percentage"
                  >
                    Percentage
                  </Button>
                  <Button
                    type="button"
                    variant={draft.mode === 'amount' ? 'default' : 'outline'}
                    aria-pressed={draft.mode === 'amount'}
                    className={`px-4 ${draft.mode === 'amount' ? 'bg-[#0047AB] hover:bg-[#003d96] text-white' : ''}`}
                    onClick={() => setDraft((prev) => ({ ...prev, mode: 'amount' }))}
                    data-testid="increment-mode-amount"
                  >
                    Amount
                  </Button>
                </div>
                <Input
                  id="increment-value"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step={draft.mode === 'amount' ? '1' : '0.01'}
                  value={draft.value}
                  onChange={(e) => setDraft((prev) => ({ ...prev, value: e.target.value }))}
                  className="font-mono"
                  placeholder={draft.mode === 'amount' ? '5000' : '10'}
                  aria-label={draft.mode === 'amount' ? 'Increment amount' : 'Increment percentage'}
                  data-testid="increment-value-input"
                  required
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {draft.mode === 'amount' ? 'Fixed amount added to basic' : 'Percent of current basic'}
                {' '}· &#8377;{fmt(currentBasic)} / month
              </p>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest font-medium text-muted-foreground">Effective month</Label>
                <Select
                  value={draft.effectiveMonth}
                  onValueChange={(value) => setDraft((prev) => ({ ...prev, effectiveMonth: value }))}
                >
                  <SelectTrigger data-testid="increment-month-select" aria-label="Effective month">
                    <SelectValue placeholder="Month" />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((name, index) => (
                      <SelectItem key={name} value={String(index + 1)}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="increment-year" className="text-xs uppercase tracking-widest font-medium text-muted-foreground">Effective year</Label>
                <Input
                  id="increment-year"
                  type="number"
                  inputMode="numeric"
                  min="2000"
                  max="2100"
                  value={draft.effectiveYear}
                  onChange={(e) => setDraft((prev) => ({ ...prev, effectiveYear: e.target.value }))}
                  className="font-mono"
                  data-testid="increment-year-input"
                  required
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="increment-reason" className="text-xs uppercase tracking-widest font-medium text-muted-foreground">Reason (optional)</Label>
              <Textarea
                id="increment-reason"
                value={draft.reason}
                onChange={(e) => setDraft((prev) => ({ ...prev, reason: e.target.value }))}
                placeholder="Annual performance increment"
                rows={3}
                data-testid="increment-reason-input"
              />
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} data-testid="increment-cancel-btn">
                Cancel
              </Button>
              <Button type="submit" className="bg-[#0047AB] hover:bg-[#003d96] text-white" data-testid="increment-save-btn">
                Save
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="space-y-4" data-testid="increment-comparison">
            <div className="rounded-lg border border-border bg-muted/40 p-3 text-sm space-y-1">
              <p>
                Basic &#8377;{fmt(preview?.current?.basic)} → <span className="font-semibold text-[#0047AB]">&#8377;{fmt(preview?.next?.basic)}</span>
                {' '}
                <span className="text-[#10b981] font-medium">(+&#8377;{fmt(addition)}{preview?.mode === 'percentage' ? `, ${preview?.value}%` : ''})</span>
              </p>
              <p className="text-muted-foreground">Effective {monthLabel} {preview?.request?.effectiveYear}</p>
              {reasonText ? <p className="text-muted-foreground">Reason: {reasonText}</p> : null}
            </div>

            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full">
                <thead>
                  <tr className="bg-muted/80">
                    <th className="py-2 px-2 sm:px-3 text-left text-[10px] sm:text-xs uppercase tracking-widest font-bold text-muted-foreground">Component</th>
                    <th className="py-2 px-2 sm:px-3 text-right text-[10px] sm:text-xs uppercase tracking-widest font-bold text-muted-foreground">Current</th>
                    <th className="py-2 px-2 sm:px-3 text-right text-[10px] sm:text-xs uppercase tracking-widest font-bold text-muted-foreground">Revised</th>
                    <th className="py-2 px-2 sm:px-3 text-right text-[10px] sm:text-xs uppercase tracking-widest font-bold text-muted-foreground">Change</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_ROWS.map((row) => {
                    const current = preview?.current?.[row.key] || 0;
                    const next = preview?.next?.[row.key] || 0;
                    const delta = next - current;
                    const changed = delta !== 0;
                    const rowTone = row.tone === 'inhand'
                      ? 'bg-[#10b981]/10'
                      : row.tone === 'ctc'
                        ? 'bg-[#0047AB]/10'
                        : changed
                          ? 'bg-amber-50/80'
                          : '';
                    return (
                      <tr key={row.key} className={rowTone} data-testid={`increment-compare-${row.key}`}>
                        <td className={`py-2 px-2 sm:px-3 text-xs sm:text-sm border-b border-border ${row.bold ? 'font-semibold' : ''}`}>
                          {row.label}
                        </td>
                        <td className="py-2 px-2 sm:px-3 border-b border-border text-right">
                          <MoneyCell monthly={current} />
                        </td>
                        <td className="py-2 px-2 sm:px-3 border-b border-border text-right">
                          {row.editable ? (
                            <div className="inline-flex flex-col items-end gap-0.5">
                              <div className="relative">
                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground font-mono">&#8377;</span>
                                <Input
                                  type="number"
                                  min="0"
                                  inputMode="numeric"
                                  aria-label={`Revised ${row.label}`}
                                  value={next}
                                  onChange={(e) => handleRevisedChange(row.key, e.target.value)}
                                  className="pl-6 font-mono text-sm h-8 w-28 sm:w-32 text-right"
                                  data-testid={`increment-revised-${row.key}`}
                                />
                              </div>
                              <span className="font-mono text-[10px] text-muted-foreground whitespace-nowrap">&#8377;{fmt(Number(next || 0) * 12)}/yr</span>
                            </div>
                          ) : (
                            <MoneyCell monthly={next} tone={row.tone} />
                          )}
                        </td>
                        <td className={`py-2 px-2 sm:px-3 border-b border-border text-right font-mono text-xs sm:text-sm whitespace-nowrap ${delta > 0 ? 'text-[#10b981]' : delta < 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
                          {delta === 0 ? '—' : `${delta > 0 ? '+' : '-'}₹${fmt(Math.abs(delta))}`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setStep('form')} disabled={saving} data-testid="increment-back-btn">
                Back
              </Button>
              <Button
                type="button"
                onClick={handleConfirm}
                disabled={saving || !employeeId}
                className="bg-[#0047AB] hover:bg-[#003d96] text-white"
                data-testid="increment-confirm-btn"
              >
                {saving ? 'Saving...' : 'Confirm'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
